// Orchestrates "Selected DTBs" as several short requests so no single call gets
// near the API Gateway timeout, however many rows are selected:
//
//   1. POST /amazon-prices/missing  which UPCs have no Amazon price, grouped by ASIN
//   2. POST /amazon-prices/fetch    fetch prices from the SP-API, one call per 20 ASINs
//   3. POST /calc-dtbs              calculate the DTBs, in chunks of UPCs
//
// Price lookup is best-effort: if it fails the DTBs are still calculated.
// Every step is idempotent (only NULL prices are written), so a cancelled or
// interrupted run can simply be started again.

export const MISSING_CHUNK = 500;
export const FETCH_CHUNK = 20; // ASINs per SP-API batch call
export const FETCH_INTERVAL_MS = 10000; // SP-API allows ~1 batch call per 10s
export const CALC_CHUNK = 25;
export const CALC_CONCURRENCY = 3;
export const MAX_THROTTLE_ATTEMPTS = 3;
export const THROTTLE_BACKOFF_MS = 15000;
export const CONFIRM_FETCH_AFTER_SECONDS = 60;

/**
 * @typedef {Object} DtbProgress
 * @property {"checking"|"fetching"|"calculating"} phase
 * @property {number} done chunks finished in this phase
 * @property {number} total chunks in this phase
 */

/**
 * @typedef {Object} DtbRunSummary
 * @property {number} requested unique UPCs selected
 * @property {number} calculated UPCs whose DTBs were calculated
 * @property {number} calcFailed UPCs in calc-dtbs chunks that failed
 * @property {number} pricesFilled ASINs that received an Amazon price
 * @property {number} rowsPriced supply rows updated with those prices
 * @property {number} noAsin UPCs with no price and no usable ASIN
 * @property {number} noOffer ASINs with no eligible Amazon price
 * @property {number} priceFailed ASINs whose lookup failed or stayed throttled
 * @property {boolean} checkFailed the missing-price check itself failed
 * @property {boolean} cancelled the run was cancelled or declined
 */

const defaultSleep = (ms, signal) =>
  new Promise((resolve) => {
    if (signal && signal.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      if (signal) signal.removeEventListener("abort", done);
      clearTimeout(timer);
      resolve();
    }
    if (signal) signal.addEventListener("abort", done);
  });

const chunk = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * @param {string[]} upcs selected UPCs
 * @param {Object} options
 * @param {(url: string, body: any) => Promise<{data: any}>} options.post
 * @param {(progress: DtbProgress) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal] stops the run after the in-flight request
 * @param {(info: {asins: number, estimatedSeconds: number}) => boolean} [options.confirmFetch]
 *   asked before a long Amazon lookup; returning false cancels the run
 * @param {(ms: number, signal?: AbortSignal) => Promise<void>} [options.sleep]
 * @returns {Promise<DtbRunSummary>}
 */
export async function calcSelectedDtbs(upcs, options) {
  const {
    post,
    onProgress = () => {},
    signal,
    confirmFetch,
    sleep = defaultSleep,
  } = options;
  const aborted = () => Boolean(signal && signal.aborted);

  const uniqueUpcs = Array.from(new Set(upcs));
  const summary = {
    requested: uniqueUpcs.length,
    calculated: 0,
    calcFailed: 0,
    pricesFilled: 0,
    rowsPriced: 0,
    noAsin: 0,
    noOffer: 0,
    priceFailed: 0,
    checkFailed: false,
    cancelled: false,
  };

  // 1. which UPCs need an Amazon price? merged by ASIN so each is fetched once
  const asinMap = new Map();
  const noAsin = new Set();
  const checkChunks = chunk(uniqueUpcs, MISSING_CHUNK);
  try {
    for (let i = 0; i < checkChunks.length; i++) {
      if (aborted()) break;
      onProgress({ phase: "checking", done: i, total: checkChunks.length });
      const { data } = await post("/amazon-prices/missing", {
        upcs: checkChunks[i],
      });
      (data.asins || []).forEach(({ asin, upcs: asinUpcs }) => {
        if (!asinMap.has(asin)) asinMap.set(asin, new Set());
        asinUpcs.forEach((upc) => asinMap.get(asin).add(upc));
      });
      (data.no_asin || []).forEach((upc) => noAsin.add(upc));
    }
  } catch (err) {
    console.log(err);
    summary.checkFailed = true;
    asinMap.clear();
  }
  summary.noAsin = noAsin.size;

  if (aborted()) {
    summary.cancelled = true;
    return summary;
  }

  // 2. fetch prices, one SP-API batch per FETCH_CHUNK unique ASINs
  const fetchChunks = chunk(Array.from(asinMap.keys()), FETCH_CHUNK);
  if (fetchChunks.length > 0) {
    const estimatedSeconds = (fetchChunks.length * FETCH_INTERVAL_MS) / 1000;
    if (
      confirmFetch &&
      estimatedSeconds > CONFIRM_FETCH_AFTER_SECONDS &&
      !confirmFetch({ asins: asinMap.size, estimatedSeconds })
    ) {
      summary.cancelled = true;
      return summary;
    }

    for (let i = 0; i < fetchChunks.length; i++) {
      if (aborted()) break;
      // pace calls to stay under the SP-API rate limit
      if (i > 0) await sleep(FETCH_INTERVAL_MS, signal);
      if (aborted()) break;
      onProgress({ phase: "fetching", done: i, total: fetchChunks.length });
      await fetchChunkPrices(fetchChunks[i], summary, {
        post,
        signal,
        sleep,
        aborted,
      });
    }
  }

  if (aborted()) {
    summary.cancelled = true;
    return summary;
  }

  // 3. calculate DTBs; chunks touch disjoint rows so a few can run at once
  const calcChunks = chunk(uniqueUpcs, CALC_CHUNK);
  let next = 0;
  let finished = 0;
  const worker = async () => {
    while (next < calcChunks.length && !aborted()) {
      const upcsChunk = calcChunks[next++];
      onProgress({
        phase: "calculating",
        done: finished,
        total: calcChunks.length,
      });
      try {
        await post("/calc-dtbs", { upcs: upcsChunk });
        summary.calculated += upcsChunk.length;
      } catch (err) {
        console.log(err);
        summary.calcFailed += upcsChunk.length;
      }
      finished++;
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(CALC_CONCURRENCY, calcChunks.length) },
      worker
    )
  );

  if (aborted()) summary.cancelled = true;
  return summary;
}

// Requests prices for one chunk of ASINs, retrying while Amazon throttles us.
// Failures are counted on the summary rather than thrown.
async function fetchChunkPrices(
  asins,
  summary,
  { post, signal, sleep, aborted }
) {
  for (let attempt = 1; attempt <= MAX_THROTTLE_ATTEMPTS; attempt++) {
    let data;
    try {
      ({ data } = await post("/amazon-prices/fetch", { asins }));
    } catch (err) {
      console.log(err);
      summary.priceFailed += asins.length;
      return;
    }

    if (!data.throttled) {
      (data.results || []).forEach((result) => {
        if (result.status === "updated") {
          summary.pricesFilled++;
          summary.rowsPriced += result.rows_updated || 0;
        } else if (result.status === "no_offer") {
          summary.noOffer++;
        } else {
          summary.priceFailed++;
        }
      });
      return;
    }

    if (attempt < MAX_THROTTLE_ATTEMPTS) {
      await sleep(THROTTLE_BACKOFF_MS, signal);
      if (aborted()) return;
    }
  }
  summary.priceFailed += asins.length;
}

/**
 * Turns a run summary into the final snackbar message and severity.
 * @param {DtbRunSummary} summary
 * @returns {{message: string, severity: "success"|"warning"|"error"}}
 */
export function summarizeDtbRun(summary) {
  const parts = [];
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

  if (summary.cancelled) {
    parts.push(
      `Cancelled. DTBs calculated for ${summary.calculated} of ${plural(
        summary.requested,
        "UPC"
      )}.`
    );
  } else {
    parts.push(
      `DTBs calculated for ${summary.calculated} of ${plural(
        summary.requested,
        "UPC"
      )}.`
    );
  }

  if (summary.pricesFilled > 0) {
    parts.push(
      `Amazon prices added for ${plural(
        summary.pricesFilled,
        "product"
      )} (${plural(summary.rowsPriced, "row")}).`
    );
  }
  if (summary.noOffer > 0 || summary.noAsin > 0) {
    parts.push(
      `${summary.noOffer + summary.noAsin} had no Amazon price available.`
    );
  }
  if (summary.checkFailed) {
    parts.push("Amazon price check failed.");
  }
  if (summary.priceFailed > 0) {
    parts.push(
      `Amazon price lookup failed for ${plural(
        summary.priceFailed,
        "product"
      )}.`
    );
  }
  if (summary.calcFailed > 0) {
    parts.push(`${plural(summary.calcFailed, "UPC")} failed to calculate.`);
  }

  let severity = "success";
  if (summary.cancelled || summary.checkFailed || summary.priceFailed > 0) {
    severity = "warning";
  }
  if (summary.calcFailed > 0) severity = "error";

  if (summary.calculated > 0) {
    parts.push("Refresh the table to see updated prices.");
  }

  return { message: parts.join(" "), severity };
}
